import { useState, useCallback, useEffect, useRef } from 'react';
import { api, ApiError } from '@/lib/api';

export interface DocumentTranslation {
  job_id: string;
  target_language: string;
  source_language: string;
  info_text: string;
  full_text: string;
  input_chars: number;
  output_chars: number;
  input_tokens: number | null;
  output_tokens: number | null;
}

interface UseDocumentTranslation {
  data: DocumentTranslation | null;
  loading: boolean;
  error: string | null;
  translate: (targetLanguage: string) => Promise<void>;
  reset: () => void;
}

/**
 * Owns the view-only whole-document translation for one job. Posts to
 * POST /api/v1/jobs/{id}/translate-document (Kaycoo translate.text under the
 * hood). The pipeline never sees the result — agents always read the source.
 */
export function useDocumentTranslation(
  jobId: string,
  onTranslated?: () => void,
): UseDocumentTranslation {
  const [data, setData] = useState<DocumentTranslation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Monotonic request id — a response is only applied if it's still the latest
  // request. The api client has no AbortController, so this guards against both
  // an overlapping re-translate and a job switch landing a stale result.
  const reqIdRef = useRef(0);

  // Clear any translation when the job changes so a pane from the previous job
  // never bleeds across. Bumping the ref also invalidates any in-flight fetch.
  useEffect(() => {
    reqIdRef.current += 1;
    setData(null);
    setError(null);
    setLoading(false);
  }, [jobId]);

  const translate = useCallback(
    async (targetLanguage: string) => {
      const reqId = ++reqIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const resp = await api.post<DocumentTranslation>(
          `/api/v1/jobs/${jobId}/translate-document`,
          { target_language: targetLanguage },
        );
        if (reqIdRef.current !== reqId) return; // superseded — drop the result
        setData(resp);
        onTranslated?.();
      } catch (err: unknown) {
        if (reqIdRef.current !== reqId) return;
        const msg =
          err instanceof ApiError
            ? err.message
            : 'Translation failed. Please retry.';
        setError(msg);
      } finally {
        if (reqIdRef.current === reqId) setLoading(false);
      }
    },
    [jobId, onTranslated],
  );

  const reset = useCallback(() => {
    reqIdRef.current += 1; // ignore any in-flight response after an explicit hide
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { data, loading, error, translate, reset };
}
